import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EmployeeScheduleView } from '../components/maintenance/EmployeeScheduleView';
import { MaintenanceModeAccess } from '../components/maintenance/MaintenanceModeAccess';

export const MySchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [prefilledMachineCode, setPrefilledMachineCode] = useState<string | undefined>();
  const [employeeCode, setEmployeeCode] = useState<string>('');

  // Get employee ID from user context
  // The JWT token includes maintenanceEmployeeId for maintenance employees
  const employeeId = (user as any)?.maintenanceEmployeeId || user?.id;

  // Fetch employee code
  React.useEffect(() => {
    const fetchEmployeeCode = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/maintenance-employees/${employeeId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setEmployeeCode(data.employeeCode);
        }
      } catch (error) {
        console.error('Error fetching employee code:', error);
      }
    };

    if (employeeId) {
      fetchEmployeeCode();
    }
  }, [employeeId]);

  const handleAccessMaintenance = (machineId: string, machineCode: string) => {
    setPrefilledMachineCode(machineCode);
    setShowAccessModal(true);
  };

  const handleAccessSuccess = (sessionId: string, machineId: string, employeeId: string) => {
    console.log('Access success, navigating to session:', sessionId);
    setShowAccessModal(false);
    // Small delay to ensure modal closes before navigation
    setTimeout(() => {
      navigate(`/maintenance-session/${sessionId}`);
    }, 100);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-orange-600">UpCar</h1>
              <span className="ml-4 text-sm text-gray-500">Manutenção</span>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">Funcionário de Manutenção</p>
                {employeeCode && (
                  <p className="text-xs font-mono font-bold text-orange-600 mt-1">
                    Código: {employeeCode}
                  </p>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Minha Agenda</h2>
              <p className="mt-1 text-sm text-gray-600">
                Visualize suas máquinas atribuídas e inicie manutenções
              </p>
            </div>

            <button
              onClick={() => setShowAccessModal(true)}
              className="inline-flex items-center px-6 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors shadow-sm"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
              </svg>
              Acessar Modo de Manutenção
            </button>
          </div>
        </div>

        {/* Schedule View */}
        <EmployeeScheduleView
          employeeId={employeeId}
          onAccessMaintenance={handleAccessMaintenance}
        />
      </main>

      {/* Access Modal */}
      {showAccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Acesso ao Modo de Manutenção</h3>
              <button
                onClick={() => {
                  setShowAccessModal(false);
                  setPrefilledMachineCode(undefined);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <MaintenanceModeAccess
              onSuccess={handleAccessSuccess}
              onCancel={() => {
                setShowAccessModal(false);
                setPrefilledMachineCode(undefined);
              }}
            />
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-gray-500">
            <p>&copy; {new Date().getFullYear()} UpCar. Todos os direitos reservados.</p>
            <p className="mt-1">
              Sistema de Gerenciamento de Manutenção
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
